import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import LeadDetailPanel from '../components/LeadDetailPanel';

interface Lead {
  id: string;
  homeowner_name: string;
  homeowner_phone: string;
  address: string;
  lead_source: string;
  job_type: string;
  hiring_position: string | null;
  created_at: string;
  lead_status: 'new' | 'contacted' | 'appointment_set' | 'lost' | 'converted' | 'sold';
  assigned_estimator: { name: string; email: string } | null;
  lead_interest: string | null;
  lead_situation: string | null;
  lead_form_notes: string | null;
}

const JOB_TYPE_META: Record<string, { label: string; color: string }> = {
  roofing:   { label: '🏠 Roofing',    color: '#4A7EB5' },
  insurance: { label: '🛡️ Insurance',  color: '#06B6D4' },
  concrete:  { label: '🪨 Concrete',    color: '#6B7280' },
  asphalt:   { label: '🛣️ Asphalt',    color: '#374151' },
  hiring:    { label: '👤 Hiring',     color: '#8B5CF6' },
};

const HIRING_POSITION_META: Record<string, { label: string; color: string }> = {
  salesman: { label: 'Salesman', color: '#8B5CF6' },
  paver: { label: 'Paver', color: '#374151' },
  roofer: { label: 'Roofer', color: '#4A7EB5' },
  foreman: { label: 'Foreman', color: '#F59E0B' },
  laborer: { label: 'Laborer', color: '#6B7280' },
  concrete: { label: 'Concrete', color: '#6B7280' },
};

const ONLINE_SOURCES = ['meta', 'google', 'website', 'facebook', 'instagram'];
const STATUS_COLUMNS: Array<{ key: 'new' | 'contacted' | 'appointment_set' | 'lost'; label: string; color: string }> = [
  { key: 'new', label: 'New', color: '#4A7EB5' },
  { key: 'contacted', label: 'Contacted', color: '#F59E0B' },
  { key: 'appointment_set', label: 'Appointment Set', color: '#10B981' },
  { key: 'lost', label: 'Lost', color: '#EF4444' },
];

const SOURCE_META: Record<string, { label: string; color: string }> = {
  facebook: { label: 'Facebook', color: '#3B82F6' },
  instagram: { label: 'Instagram', color: '#EC4899' },
  website: { label: 'Website', color: '#10B981' },
  google: { label: 'Google', color: '#F59E0B' },
  meta: { label: 'Meta', color: '#4A7EB5' },
};

const timeAgo = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.max(1, Math.floor(diffMs / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

export default function LeadsPage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [estimators, setEstimators] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [filterJobType, setFilterJobType] = useState('all');

  const fetchLeads = async () => {
    try {
      const { data } = await api.get('/leads/online');
      setLeads((data || []).filter((lead: Lead) => ONLINE_SOURCES.includes(lead.lead_source)));
    } catch (error) {
      console.error('Failed to fetch online leads:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    api.get('/users').then(r => setEstimators((r.data || []).filter((u: any) => ['estimator','admin'].includes(u.role)))).catch(() => {});
  }, []);

  const updateLeadStatus = async (leadId: string, lead_status: Lead['lead_status']) => {
    try {
      const { data } = await api.patch(`/leads/${leadId}/online-status`, { lead_status });
      setLeads((prev) => prev.map((lead) => (lead.id === leadId ? { ...lead, ...data } : lead)));
    } catch (error) {
      console.error('Failed to update lead status:', error);
    }
  };

  const filteredLeads = useMemo(() => {
    return leads.filter(l => {
      if (l.lead_status === 'converted' || l.lead_status === 'sold') return false;
      if (filterSource !== 'all' && l.lead_source !== filterSource) return false;
      if (filterJobType !== 'all' && l.job_type !== filterJobType) return false;
      if (search) {
        const q = search.toLowerCase();
        return (l.homeowner_name || '').toLowerCase().includes(q) ||
               (l.address || '').toLowerCase().includes(q) ||
               (l.homeowner_phone || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [leads, filterSource, filterJobType, search]);

  const stats = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());

    return {
      newToday: leads.filter((l) => l.lead_status === 'new' && new Date(l.created_at) >= startOfToday).length,
      thisWeek: leads.filter((l) => new Date(l.created_at) >= startOfWeek).length,
      contacted: leads.filter((l) => l.lead_status === 'contacted').length,
      apptSet: leads.filter((l) => l.lead_status === 'appointment_set').length,
      lost: leads.filter((l) => l.lead_status === 'lost').length,
      facebook: leads.filter((l) => l.lead_source === 'facebook' || l.lead_source === 'meta').length,
      instagram: leads.filter((l) => l.lead_source === 'instagram').length,
      website: leads.filter((l) => l.lead_source === 'website').length,
      google: leads.filter((l) => l.lead_source === 'google').length,
    };
  }, [leads]);

  return (
    <>
    <div style={{ padding: 24, background: '#0F1923', minHeight: '100%' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ color: '#fff', fontSize: 30, fontWeight: 800, margin: 0 }}>📥 Lead Center</h1>
        <p style={{ color: '#8FA8C0', marginTop: 6, marginBottom: 0 }}>Online inbound leads</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(130px, 1fr))', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'New Today', value: stats.newToday, color: '#4A7EB5' },
          { label: 'This Week', value: stats.thisWeek, color: '#4A7EB5' },
          { label: 'Contacted', value: stats.contacted, color: '#F59E0B' },
          { label: 'Appt Set', value: stats.apptSet, color: '#10B981' },
          { label: 'Lost', value: stats.lost, color: '#EF4444' },
        ].map((stat) => (
          <div key={stat.label} style={{ background: '#1A2B3C', border: '1px solid #1E3A5F', borderRadius: 12, padding: 14 }}>
            <div style={{ color: stat.color, fontSize: 28, fontWeight: 800 }}>{stat.value}</div>
            <div style={{ color: '#8FA8C0', fontSize: 12 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Search + Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search name, address, phone..."
          style={{ flex: 1, minWidth: 200, background: '#1A2B3C', border: '1px solid #1E3A5F', color: '#fff', padding: '10px 14px', borderRadius: 10, fontSize: 14 }}
        />
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)} style={{ background: '#1A2B3C', border: '1px solid #1E3A5F', color: '#fff', padding: '10px 12px', borderRadius: 10, fontSize: 13 }}>
          <option value="all">All Sources</option>
          <option value="facebook">Facebook</option>
          <option value="instagram">Instagram</option>
          <option value="google">Google</option>
          <option value="website">Website</option>
        </select>
        <select value={filterJobType} onChange={e => setFilterJobType(e.target.value)} style={{ background: '#1A2B3C', border: '1px solid #1E3A5F', color: '#fff', padding: '10px 12px', borderRadius: 10, fontSize: 13 }}>
          <option value="all">All Job Types</option>
          <option value="roofing">🏠 Roofing</option>
          <option value="concrete">🪨 Concrete</option>
          <option value="asphalt">🛣️ Asphalt</option>
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { key: 'facebook', value: stats.facebook },
          { key: 'instagram', value: stats.instagram },
          { key: 'website', value: stats.website },
          { key: 'google', value: stats.google },
        ].map((source) => (
          <div key={source.key} style={{ background: '#1A2B3C', border: '1px solid #1E3A5F', borderRadius: 12, padding: 14 }}>
            <div style={{ color: SOURCE_META[source.key].color, fontSize: 24, fontWeight: 800 }}>{source.value}</div>
            <div style={{ color: '#8FA8C0', fontSize: 12 }}>{SOURCE_META[source.key].label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(280px, 1fr))', gap: 14, alignItems: 'start', overflowX: 'auto' }}>
        {STATUS_COLUMNS.map((column) => {
          const columnLeads = filteredLeads.filter((lead) => (lead.lead_status || 'new') === column.key);
          return (
            <div key={column.key} style={{ background: '#1A2B3C', border: '1px solid #1E3A5F', borderRadius: 14, minHeight: 560 }}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid #1E3A5F', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#fff', fontWeight: 700 }}>{column.label}</span>
                <span style={{ background: `${column.color}22`, border: `1px solid ${column.color}55`, color: column.color, fontWeight: 700, padding: '2px 10px', borderRadius: 999 }}>
                  {columnLeads.length}
                </span>
              </div>

              <div style={{ maxHeight: 520, overflowY: 'auto', padding: 10 }}>
                {columnLeads.map((lead) => {
                  const source = SOURCE_META[lead.lead_source] || { label: lead.lead_source || 'Unknown', color: '#4A7EB5' };
                  return (
                    <div key={lead.id} onClick={() => setSelectedLeadId(lead.id)} style={{ background: '#0F1923', border: '1px solid #1E3A5F', borderRadius: 12, padding: 12, marginBottom: 10, cursor: 'pointer', transition: 'border-color 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.borderColor = '#4A7EB5')}
                      onMouseLeave={e => (e.currentTarget.style.borderColor = '#1E3A5F')}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ background: `${source.color}22`, color: source.color, border: `1px solid ${source.color}55`, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                          {source.label}
                        </span>
                        <span style={{ color: '#8FA8C0', fontSize: 11 }}>{timeAgo(lead.created_at)}</span>
                      </div>
                      {lead.job_type && (() => {
                        const jt = JOB_TYPE_META[lead.job_type] || { label: lead.job_type, color: '#4A7EB5' };
                        // For hiring leads, show position if available
                        const hiringPos = lead.job_type === 'hiring' && lead.hiring_position 
                          ? HIRING_POSITION_META[lead.hiring_position] || { label: lead.hiring_position, color: '#8B5CF6' }
                          : null;
                        const displayLabel = hiringPos ? `👤 Hiring - ${hiringPos.label}` : jt.label;
                        return (
                          <span style={{ background: `${jt.color}22`, color: jt.color, border: `1px solid ${jt.color}55`, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, marginBottom: 6, display: 'inline-block' }}>
                            {displayLabel}
                          </span>
                        );
                      })()}

                      <div style={{ color: '#fff', fontSize: 16, fontWeight: 800 }}>{lead.homeowner_name || 'Unknown Homeowner'}</div>
                      <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 4 }}>{lead.address || 'No address'}</div>
                      <div style={{ color: '#8FA8C0', fontSize: 12 }}>{lead.homeowner_phone || 'No phone'}</div>
                      {lead.lead_interest && (
                        <div style={{ color: '#60A5FA', fontSize: 11, marginTop: 4 }}>🎯 {lead.lead_interest.replace(/_/g, ' ')}</div>
                      )}
                      {lead.lead_situation && (
                        <div style={{ color: '#A78BFA', fontSize: 11 }}>💬 {lead.lead_situation.replace(/_/g, ' ')}</div>
                      )}
                      <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: lead.assigned_estimator?.name ? '#8FA8C0' : '#EF4444' }}>
                        Estimator: {lead.assigned_estimator?.name || 'Unassigned'}
                      </div>

                      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateLeadStatus(lead.id, 'contacted');
                          }}
                          style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #1E3A5F', background: '#1A2B3C', color: '#fff', fontSize: 11, cursor: 'pointer' }}
                        >
                          Contacted ✓
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateLeadStatus(lead.id, 'appointment_set');
                          }}
                          style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #1E3A5F', background: '#1A2B3C', color: '#fff', fontSize: 11, cursor: 'pointer' }}
                        >
                          Appt Set ✓
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!window.confirm(`Mark ${lead.homeowner_name || 'this lead'} as Lost?`)) return;
                            updateLeadStatus(lead.id, 'lost');
                          }}
                          style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #EF444466', background: '#EF444411', color: '#EF4444', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                        >
                          Lost ✗
                        </button>
                      </div>
                    </div>
                  );
                })}

                {!loading && columnLeads.length === 0 && (
                  <div style={{ color: '#4A6580', textAlign: 'center', padding: '28px 8px' }}>No leads in this stage</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>

      {/* Lead Detail Panel */}
      <LeadDetailPanel
        leadId={selectedLeadId}
        onClose={() => setSelectedLeadId(null)}
        estimators={estimators}
        onUpdated={(updated) => setLeads(prev => prev.map(l => l.id === updated.id ? { ...l, ...updated } as Lead : l))}
      />
    </>
  );
}
